<script>
import { mapState } from "vuex";
import Layout from "@/router/layouts/main";
import EmptyState from "@/components/widgets/empty-state";
import StPageHeader from "@/components/base/st-page-header";
import StKpiCard from "@/components/base/st-kpi-card";
import StMetaProgress from "@/components/base/st-meta-progress";
import StResponsiveList from "@/components/base/st-responsive-list";
import MetasSelosHistorico from "@/components/metas/metas-selos-historico";
import AvisoTruck from "@/components/metas/aviso-truck";
import { api } from "@/state/services";
import {
  carregarConfigMetas,
  chaveUsuario,
  produtosVisiveis,
  isSecretaria,
  mesAno,
  nomeMes,
  fmtData,
  fmtDataCurta,
  fmtDataHora,
  plural,
  fmtMoeda,
  classeStatus,
  textoPrazo,
} from "@/utils/minhas-metas";
import { carregarJornada, mesclarJornada } from "@/utils/jornada";

/**
 * Minhas Metas — painel de acompanhamento (APVS Brasil / Truck).
 * Piloto do novo padrão visual: StPageHeader, StKpiCard, StMetaProgress, StResponsiveList.
 * Toda a regra (janelas, prazos, status, níveis) vem pronta da API (GET metas/painel).
 */
export default {
  components: { Layout, EmptyState, StPageHeader, StKpiCard, StMetaProgress, StResponsiveList, MetasSelosHistorico, AvisoTruck },
  data() {
    return {
      config: null,
      erroConfig: "",
      produto: null,
      paineis: {},
      carregando: {},
      erroCopia: false,
      erro: "",
      filtroP1: "todos",
      copiado: false,
      salvandoJornada: false,
      erroJornada: "",
    };
  },
  computed: {
    ...mapState({ account: (state) => state.account }),
    secretaria() {
      return isSecretaria(this.account.user);
    },
    produtos() {
      return produtosVisiveis(this.config);
    },
    // Estado da Jornada diária (cache do Vuex por usuário+dia — ver utils/jornada.js).
    jornada() {
      const j = this.account && this.account.jornadaEstado;
      return j && j.data && j.key === chaveUsuario(this.account.user) ? j.data : null;
    },
    painel() {
      return this.produto ? this.paineis[this.produto] || null : null;
    },
    planejado() {
      return !!(this.config && this.config.planejamento && this.config.planejamento.configurado);
    },
    isRegional() {
      return !!(this.config && this.config.perfil && this.config.perfil.tipo === "regional");
    },
    nomeProduto() {
      return this.produto === "truck" ? "APVS Truck" : "APVS Brasil";
    },
    subtitulo() {
      if (!this.painel) return "Acompanhe as adesões que contam para a sua meta.";
      const f = this.painel.passo1.fechamento;
      return "Fechamento de " + mesAno(f.mes, f.ano) + " · " + (this.isRegional ? "visão da sua regional" : "sua produção");
    },
    atualizacao() {
      if (!this.painel || !this.painel.atualizacao) return "";
      const a = this.painel.atualizacao;
      const partes = [];
      if (a.adesoes) partes.push("adesões em " + fmtDataHora(a.adesoes));
      if (a.metas) partes.push("metas em " + fmtDataHora(a.metas));
      return partes.length ? "Dados atualizados: " + partes.join(" · ") : "";
    },
    p1() {
      return this.painel ? this.painel.passo1 : null;
    },
    p2() {
      return this.painel ? this.painel.passo2 : null;
    },
    p3() {
      return this.painel ? this.painel.passo3 : null;
    },
    regras() {
      return this.painel ? this.painel.regras : null;
    },
    gami() {
      return this.painel ? this.painel.gamificacao : null;
    },
    rotuloFechamento() {
      return this.p1 ? mesAno(this.p1.fechamento.mes, this.p1.fechamento.ano) : "";
    },
    rotuloVigencia() {
      return this.p1 ? mesAno(this.p1.vigencia.mes, this.p1.vigencia.ano) : "";
    },
    itensP1() {
      if (!this.p1) return [];
      const f = this.filtroP1;
      if (f === "todos") return this.p1.itens;
      // Mesmo critério do KPI "Pendentes" (pendente + pago aguardando processamento).
      if (f === "pendentes") return this.p1.itens.filter((i) => ["pendente", "pago_aguardando"].indexOf(i.status.key) !== -1);
      if (f === "perdidas") return this.p1.itens.filter((i) => i.status.key === "estourado");
      return this.p1.itens.filter((i) => i.status.key === "contando");
    },
    placasPendentes() {
      if (!this.p1) return [];
      return this.p1.itens
        .filter((i) => i.status.key === "pendente" && i.placa)
        .map((i) => i.placa);
    },
    marcadoresP1() {
      if (!this.regras) return [];
      const m = [{ valor: this.regras.recomendado, label: "Gordura" }];
      if (this.regras.meta_pessoal != null && this.regras.meta_pessoal !== this.regras.recomendado) {
        m.push({ valor: this.regras.meta_pessoal, label: "Sua meta" });
      }
      return m;
    },
    camposP1() {
      const c = [
        { key: "placa", label: "Placa", titulo: true },
        { key: "associado", label: "Associado" },
      ];
      if (this.isRegional) c.push({ key: "consultor", label: "Consultor" });
      c.push(
        { key: "vencimento", label: "Vencimento" },
        { key: "prazo", label: "Prazo" },
        { key: "status", label: "Situação" }
      );
      return c;
    },
    camposP2() {
      const c = [
        { key: "placa", label: "Placa", titulo: true },
        { key: "associado", label: "Associado" },
      ];
      if (this.isRegional) c.push({ key: "consultor", label: "Consultor" });
      c.push(
        { key: "vigencia", label: "Início vigência" },
        { key: "vencimento", label: "Vencimento" },
        { key: "status", label: "Situação" }
      );
      return c;
    },
    camposP3() {
      return [
        { key: "nome", label: "Nome", titulo: true },
        { key: "nivel", label: "Nível", align: "right" },
        { key: "p1_total", label: "Fech. total", align: "right" },
        { key: "p1_contando", label: "Contando", align: "right" },
        { key: "p1_pendentes", label: "Pendentes", align: "right" },
      ];
    },
    // Sem a coluna "No mês": quem só tem adesões do mês atual (nada no fechamento) sai da lista.
    itensP3() {
      return this.p3 ? this.p3.itens.filter((i) => i.p1_total > 0) : [];
    },
    faixa() {
      return this.regras ? this.regras.faixa_regional : null;
    },
    equipe() {
      return this.p3 ? this.p3.equipe : null;
    },
    corte() {
      return this.p1 ? this.p1.corte : null;
    },
    // Prévia do Passo 3: quanto recebe / ainda pode receber / está deixando de receber. Os números
    // e a situação vêm prontos da API (Model_minhas_metas::previaComissao); aqui só o texto.
    comissao() {
      const c = this.p1 && this.p1.comissao;
      if (!c || !c.topo) return null;
      const topo = c.topo;
      const alc = c.alcancavel;
      const atual = c.atual;
      const meta = (f) => f.percentual + "% (" + f.minimo + " " + plural(f.minimo, "adesão", "adesões") + ")";
      const base = {};
      // Sem boleto pago no mês não há valor a mostrar; só a mensagem de "próximo fechamento" dispensa valor.
      if (!(c.faturamento && c.faturamento.total) && c.situacao !== "sem_chance") return null;

      if (c.situacao === "maximo") {
        return { ...base, tom: "ok", rotulo: "Você garantiu", valor: atual.valor,
          linhas: ["Parabéns! Você já atingiu " + meta(topo) + " com " + c.contando + " " + plural(c.contando, "adesão convertida", "adesões convertidas") + "."] };
      }
      // Já garantiu uma faixa E as pendentes ainda no prazo levam a uma faixa maior: verde com o
      // garantido + vermelho com o que fica "na mesa" se não converter. O teto vem das faixas da
      // modalidade (Home Office até 7%, Executiva ou acima até 10%) e o alvo é só a faixa que o
      // potencial alcança — sem pendente que mude a faixa, o aviso vermelho não aparece.
      if (atual && alc && alc.percentual > atual.percentual) {
        const pendentes = Math.max(0, c.potencial - c.contando);
        return { ...base, tom: "ok", rotulo: "Você garantiu", valor: atual.valor,
          linhas: ["Parabéns! Você já atingiu " + meta(atual) + " com " + c.contando + " " +
            plural(c.contando, "adesão convertida", "adesões convertidas") + "."],
          naMesa: {
            valor: Math.round((alc.valor - atual.valor) * 100) / 100,
            linhas: [
              "Você ainda tem " + pendentes + " " + plural(pendentes, "adesão pendente", "adesões pendentes") +
                " no prazo. Convertendo mais " + alc.faltam + " com o 1º boleto pago, você sobe para " +
                meta(alc) + " e recebe " + fmtMoeda(alc.valor) + ".",
              "Se não converter, você deixa de receber " + fmtMoeda(alc.valor - atual.valor) + ". Corra atrás!",
            ],
          } };
      }
      if (c.situacao === "chance_topo") {
        const linhas = [
          "Você tem " + c.contando + " das " + topo.minimo + " adesões convertidas: faltam " + topo.faltam +
            " com o 1º boleto pago para receber " + fmtMoeda(topo.valor) + " (" + topo.percentual + "%).",
        ];
        return { ...base, tom: "chance", rotulo: "Você ainda pode receber", valor: topo.valor, linhas };
      }
      // O "deixando na mesa" só aparece no bloco acima (faixa já garantida + pendentes que sobem a
      // faixa). Quando não há como subir, não se mostra perda nem diferença entre faixas.
      if (c.situacao === "chance_menor") {
        return { ...base, tom: "chance", rotulo: "Você ainda pode receber", valor: alc.valor,
          linhas: [
            "Você tem " + c.contando + " das " + alc.minimo + " adesões convertidas: faltam " + alc.faltam +
              " com o 1º boleto pago para receber " + fmtMoeda(alc.valor) + " (" + alc.percentual + "%).",
          ] };
      }
      if (c.situacao === "garantido_menor") {
        return { ...base, tom: "ok", rotulo: "Você garantiu", valor: atual.valor,
          linhas: [
            "Você atingiu " + atual.percentual + "% com " + c.contando + " " +
              plural(c.contando, "adesão convertida", "adesões convertidas") + ".",
          ] };
      }
      return { ...base, tom: "chance", rotulo: "Foco no próximo fechamento", valor: null,
        linhas: [
          "Neste fechamento a meta de comissão ficou fora de alcance. Cada adesão que você trouxer agora já soma para o próximo — bora pra cima!",
        ] };
    },
  },
  watch: {
    // Depois de "Refazer meu planejamento" o wizard atualiza o cache no Vuex: recarrega a meta
    // pessoal (e a aba Truck, que depende de "produz Truck").
    // A 1ª carga (config ainda nula) é tratada em carregarConfig(); aqui só reagimos a mudança
    // real do planejamento, para não disparar o GET do painel duas vezes.
    "account.metasConfig"(novo) {
      if (!novo || !novo.data || !this.config || novo.data === this.config) return;
      const antes = JSON.stringify(this.config.planejamento || {});
      const depois = JSON.stringify(novo.data.planejamento || {});
      this.config = novo.data;
      if (antes === depois) return;
      if (this.produtos.indexOf(this.produto) === -1) this.produto = this.produtos[0] || null;
      this.paineis = {};
      if (this.produto) this.carregarPainel(this.produto);
    },
  },
  created() {
    this.carregarConfig();
    if (!this.secretaria) {
      carregarJornada(this.$store).catch(() => {
        // Silencioso: sem o SQL aplicado (disponivel=false) o toggle só não aparece.
      });
    }
  },
  methods: {
    nomeMes,
    fmtData,
    fmtDataCurta,
    plural,
    fmtMoeda,
    classeStatus,
    textoPrazo,
    carregarConfig() {
      this.erroConfig = "";
      carregarConfigMetas(this.$store)
        .then((cfg) => {
          this.config = cfg;
          this.produto = this.produtos[0] || null;
          if (this.produto) this.carregarPainel(this.produto);
        })
        .catch((e) => {
          this.erroConfig = (e && e.message) || "Não foi possível carregar suas metas.";
        });
    },
    trocarProduto(p) {
      if (p === this.produto) return;
      this.produto = p;
      this.filtroP1 = "todos";
      if (!this.paineis[p]) this.carregarPainel(p);
    },
    carregarPainel(p) {
      this.$set(this.carregando, p, true);
      this.erro = "";
      api
        .get("metas/painel?produto=" + encodeURIComponent(p))
        .then((response) => {
          const d = response && response.data;
          if (!d || d.status !== "success") {
            this.erro = (d && d.message) || "Não foi possível carregar suas metas.";
            return;
          }
          this.$set(this.paineis, p, d);
        })
        .catch((error) => {
          this.erro =
            error && error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "Não foi possível carregar suas metas agora. Confira a conexão e tente de novo.";
        })
        .finally(() => {
          this.$set(this.carregando, p, false);
        });
    },
    recarregar() {
      if (this.produto) {
        this.$delete(this.paineis, this.produto);
        this.carregarPainel(this.produto);
      } else {
        this.carregarConfig();
      }
    },
    refazer() {
      this.$store.commit("account/setMetasWizard", true);
    },
    alternarJornada(valor) {
      if (this.salvandoJornada) return;
      const ativa = !!valor;
      this.salvandoJornada = true;
      this.erroJornada = "";
      api
        .put("metas/jornada", { ativa: ativa ? "yes" : "no" })
        .then((response) => {
          const d = response && response.data;
          if (!d || d.status !== "success") {
            this.erroJornada = (d && d.message) || "Não foi possível salvar agora.";
            return;
          }
          mesclarJornada(this.$store, { ativa: d.ativa });
        })
        .catch(() => {
          this.erroJornada = "Sem conexão com o servidor. Tente de novo.";
        })
        .finally(() => {
          this.salvandoJornada = false;
        });
    },
    reverJornada() {
      this.$store.commit("account/setJornadaReplay", true);
    },
    copiarPlacas() {
      const texto = this.placasPendentes.join("\n");
      if (!texto) return;
      this.erroCopia = false;
      const concluir = () => {
        this.copiado = true;
        setTimeout(() => {
          this.copiado = false;
        }, 2500);
      };
      // vue-clipboard2 já usa o fallback de textarea + execCommand (WebView / HTTP).
      const tentativa = this.$copyText
        ? this.$copyText(texto)
        : navigator.clipboard
        ? navigator.clipboard.writeText(texto)
        : Promise.reject(new Error("sem clipboard"));
      tentativa.then(concluir).catch(() => {
        this.erroCopia = true;
      });
    },
    classeLinhaP1(item) {
      const s = item.status || {};
      if (s.key === "estourado") return "mm-row-bad";
      if (s.alerta) return "mm-row-warn";
      return null;
    },
    textoSituacaoEquipe() {
      const e = this.equipe;
      if (!e || !this.regras) return "";
      const regrasProduto = (this.config && this.config.regras && this.config.regras[this.produto]) || {};
      const pct = (regrasProduto.percentual_consultor || 5) + "%";
      if (e.situacao === "sozinho") {
        return "Você já bate a meta sozinho: recebe " + pct + " da própria base e dos consultores abaixo que não bateram.";
      }
      if (e.situacao === "equipe") {
        return "Somando as metas dos consultores abaixo que ainda não bateram, você chega a " + e.total_projetado +
          " — bateria em equipe (" + pct + " somente da própria base).";
      }
      if (e.propria === 0) {
        return "Para bater em equipe você precisa de pelo menos 1 meta própria contando.";
      }
      return "Faltam " + e.faltam + " para bater a meta, mesmo somando a equipe.";
    },
  },
};
</script>

<template>
  <Layout>
    <div class="mm-page">
      <StPageHeader title="Minhas Metas" :subtitle="subtitulo" :breadcrumb="[{ text: 'Minhas Metas' }]">
        <template v-if="atualizacao" #meta>
          <i class="bx bx-time-five" aria-hidden="true"></i> {{ atualizacao }}
        </template>
        <template v-if="config && !secretaria" #actions>
          <button type="button" class="btn btn-outline-default" @click="refazer">
            <i class="bx bx-edit-alt mr-1" aria-hidden="true"></i>
            {{ planejado ? "Refazer meu planejamento" : "Fazer meu planejamento" }}
          </button>
        </template>
      </StPageHeader>

      <!-- Erro ao carregar a configuração -->
      <div v-if="erroConfig" class="card">
        <div class="card-body">
          <EmptyState icon="bx bx-error-circle" title="Não foi possível carregar suas metas" :text="erroConfig">
            <template #action>
              <button type="button" class="btn btn-default" @click="carregarConfig">Tentar de novo</button>
            </template>
          </EmptyState>
        </div>
      </div>

      <!-- Carregando configuração -->
      <div v-else-if="!config" class="mm-kpis" aria-busy="true">
        <StKpiCard v-for="n in 4" :key="n" label="Carregando" loading />
      </div>

      <!-- Sem APVS -->
      <div v-else-if="!produtos.length" class="card">
        <div class="card-body">
          <EmptyState
            icon="bx bx-car"
            title="Minhas Metas é para quem produz APVS"
            text="Sua conta não tem APVS Brasil nem APVS Truck ativos."
          />
        </div>
      </div>

      <template v-else>
        <!-- Convite para planejar -->
        <div v-if="!planejado && !secretaria" class="mm-convite" role="note">
          <i class="bx bx-bulb" aria-hidden="true"></i>
          <div>
            <strong>Você ainda não definiu sua meta pessoal.</strong>
            Enquanto isso, usamos o mínimo recomendado. Leva 2 minutos.
          </div>
          <button type="button" class="btn btn-default btn-sm" @click="refazer">Planejar agora</button>
        </div>

        <!-- Jornada diária: liga/desliga + rever agora (só titular) -->
        <div v-if="!secretaria && jornada && jornada.disponivel" class="mm-jornada" role="group" aria-labelledby="mm-jornada-titulo">
          <span class="mm-jornada-icone" aria-hidden="true"><i class="bx bx-map-alt"></i></span>
          <div class="mm-jornada-texto">
            <strong id="mm-jornada-titulo">Jornada diária</strong>
            <span>Na 1ª abertura do dia, o app apresenta suas metas em fases, passo a passo.</span>
            <span v-if="erroJornada" class="mm-jornada-erro" role="alert">{{ erroJornada }}</span>
          </div>
          <div class="mm-jornada-acoes">
            <b-form-checkbox
              switch
              :checked="jornada.ativa"
              :disabled="salvandoJornada"
              aria-describedby="mm-jornada-titulo"
              @change="alternarJornada"
            >
              {{ jornada.ativa ? "Ativada" : "Desativada" }}
            </b-form-checkbox>
            <button
              type="button"
              class="btn btn-light btn-sm"
              :disabled="!planejado || !painel"
              :title="planejado ? '' : 'Faça seu planejamento primeiro'"
              @click="reverJornada"
            >
              <i class="bx bx-play-circle mr-1" aria-hidden="true"></i> Rever jornada agora
            </button>
          </div>
        </div>

        <!-- Abas de produto -->
        <div v-if="produtos.length > 1" class="mm-tabs" role="group" aria-label="Produto">
          <button
            v-for="p in produtos"
            :key="p"
            type="button"
            class="mm-tab"
            :class="{ ativo: p === produto }"
            :aria-pressed="p === produto ? 'true' : 'false'"
            @click="trocarProduto(p)"
          >
            {{ p === "truck" ? "APVS Truck" : "APVS Brasil" }}
          </button>
        </div>

        <!-- Erro do painel -->
        <div v-if="erro && !painel" class="card">
          <div class="card-body">
            <EmptyState icon="bx bx-error-circle" title="Não foi possível carregar o painel" :text="erro">
              <template #action>
                <button type="button" class="btn btn-default" @click="recarregar">Tentar de novo</button>
              </template>
            </EmptyState>
          </div>
        </div>

        <!-- Carregando painel -->
        <div v-else-if="!painel" class="mm-kpis" aria-busy="true">
          <StKpiCard v-for="n in 4" :key="n" label="Carregando" loading />
        </div>

        <template v-else-if="painel">
          <AvisoTruck v-if="produto === 'truck'" />

          <div v-if="painel.truncado" class="mm-aviso warning" role="status">
            A sua equipe tem muitas adesões neste período: mostramos as mais recentes. Os totais podem estar incompletos.
          </div>

          <!-- PASSO 1 — Adesões do mês (meta pessoal) -->
          <section class="card" aria-labelledby="mm-p2-titulo">
            <div class="card-body">
              <p class="mm-passo">Passo 1</p>
              <h2 id="mm-p2-titulo" class="mm-h2">Adesões de {{ nomeMes(p2.mes) }} (sua meta pessoal)</h2>

              <div class="mm-kpis">
                <StKpiCard label="Feitas no mês" :value="p2.feitas" icon="bx bx-plus-circle" variant="brand" />
                <StKpiCard label="Meta pessoal" :value="p2.meta_pessoal" icon="bx bx-target-lock" variant="info"
                  :hint="regras.meta_pessoal == null ? 'mínimo recomendado' : 'definida por você'" />
                <StKpiCard label="Dias restantes" :value="p2.dias_restantes" icon="bx bx-calendar" variant="neutral" hint="contando hoje" />
                <StKpiCard label="Por dia" :value="p2.faltam ? p2.por_dia.toLocaleString('pt-BR') : 0" icon="bx bx-trending-up"
                  :variant="p2.faltam ? 'warning' : 'success'"
                  :hint="p2.faltam ? 'para chegar em ' + p2.meta_pessoal + ' (faltam ' + p2.faltam + ')' : 'meta pessoal atingida'" />
              </div>

              <StMetaProgress class="mb-3" :atual="p2.feitas" :meta="p2.meta_pessoal" label="Progresso da meta pessoal" />

              <p class="mm-nota">
                <i class="bx bx-info-circle" aria-hidden="true"></i>
                <span>
                  Estas adesões podem contar no fechamento dos <strong>2 meses seguintes</strong>, mas o 1º boleto
                  tem que ser pago até o dia {{ regras.dia_corte }} do mês do fechamento e sem passar de
                  {{ regras.dias_apos_vencimento }} dias do vencimento.
                </span>
              </p>

              <StResponsiveList
                :fields="camposP2"
                :items="p2.itens"
                caption="Adesões do mês atual"
                empty-icon="bx bx-calendar-plus"
                :empty-title="'Nenhuma adesão em ' + nomeMes(p2.mes) + ' ainda'"
                empty-text="As adesões aparecem aqui depois que o relatório do dia é processado."
              >
                <template #cell(vigencia)="{ item }">{{ fmtData(item.vigencia) }}</template>
                <template #cell(vencimento)="{ item }">{{ fmtData(item.vencimento) }}</template>
                <template #cell(status)="{ item }">
                  <span class="mm-badge" :class="classeStatus(item.status.key)">
                    {{ item.status.key === "pendente" ? item.situacao_parcela || item.status.label : item.status.label }}
                  </span>
                </template>
              </StResponsiveList>
            </div>
          </section>

          <!-- PASSO 2 — Fechamento (gamificação) -->
          <section class="card mm-gami" aria-labelledby="mm-gami-titulo">
            <div class="card-body">
              <div class="mm-gami-top">
                <div>
                  <p class="mm-passo">Passo 2</p>
                  <h2 id="mm-gami-titulo" class="mm-h2">Fechamento de {{ rotuloFechamento }}</h2>
                  <p class="mm-muted mb-0">{{ nomeProduto }} · meta {{ regras.meta_oficial }}</p>
                </div>
                <span class="mm-nivel" :class="'mm-nivel-' + gami.nivel.key">
                  <i class="bx bx-medal" aria-hidden="true"></i> {{ gami.nivel.label }}
                </span>
              </div>

              <StMetaProgress
                class="mt-3"
                :atual="p1.resumo.contando"
                :meta="regras.meta_oficial"
                :marcadores="marcadoresP1"
                label="Adesões já contando"
              />

              <MetasSelosHistorico
                :gamificacao="gami"
                :legenda="isRegional ? 'Contagem da regional (equipe + você).' : 'Contagem da sua produção própria (não considera a regra de equipe).'"
              />
            </div>
          </section>

          <!-- PASSO 3 — Contam no Fechamento -->
          <section class="card" aria-labelledby="mm-p1-titulo">
            <div class="card-body">
              <p class="mm-passo">Passo 3</p>
              <h2 id="mm-p1-titulo" class="mm-h2">Contam no Fechamento de {{ rotuloFechamento }}</h2>
              <p class="mm-muted">
                Adesões com início de vigência em <strong>{{ rotuloVigencia }}</strong>. Elas contam para a meta
                assim que o 1º boleto é pago (até {{ regras.dias_apos_vencimento }} dias após o vencimento e até o dia {{ regras.dia_corte }}).
                <template v-if="p1.vigencia_anterior">
                  Também aparecem as de <strong>{{ nomeMes(p1.vigencia_anterior.mes) }}</strong> que perderam o corte
                  mas ainda estão dentro dos {{ regras.dias_apos_vencimento }} dias após o vencimento — se pagarem a tempo,
                  contam neste fechamento.
                </template>
              </p>

              <div
                class="mm-corte"
                :class="{ urgente: corte.periodo_corte || corte.dias_restantes <= regras.alerta_dias }"
                role="note"
              >
                <i class="bx bx-calendar-event" aria-hidden="true"></i>
                <div>
                  <strong>Corte: {{ fmtData(corte.data) }} ({{ corte.dia_semana }})</strong>
                  <template v-if="corte.dias_restantes > 0"> · faltam {{ corte.dias_restantes }} {{ plural(corte.dias_restantes, "dia", "dias") }}</template>
                  <template v-else-if="corte.dias_restantes === 0"> · é hoje</template>
                  <div v-if="!corte.dia_util" class="mm-corte-extra">
                    O dia {{ regras.dia_corte }} cai em <strong>{{ corte.motivo }}</strong>: pagamento feito nesse dia só compensa no
                    próximo dia útil. Oriente o pagamento até <strong>{{ fmtData(corte.pagar_ate) }} ({{ corte.pagar_ate_dia_semana }})</strong>.
                  </div>
                  <div v-else-if="corte.periodo_corte" class="mm-corte-extra">
                    Estamos na semana do corte: quem ainda não pagou o 1º boleto precisa pagar até o dia {{ regras.dia_corte }}.
                  </div>
                  <div class="mm-corte-extra mm-corte-atencao">
                    <strong>Atenção a fins de semana e feriados:</strong> pagamento feito em sábado, domingo ou feriado só é
                    compensado pelo banco no próximo dia útil. Se essa compensação cair <strong>depois do dia {{ regras.dia_corte }}</strong>,
                    a adesão <strong>não conta para a meta deste fechamento</strong>. Oriente o associado a pagar com antecedência,
                    em dia útil. Feriados estaduais e municipais também atrasam a compensação — o aviso acima considera só os
                    nacionais.
                  </div>
                </div>
              </div>

              <div class="mm-kpis">
                <StKpiCard label="Total" :value="p1.resumo.total" icon="bx bx-list-ul" variant="brand"
                  :hint="p1.resumo.canceladas ? p1.resumo.canceladas + ' cancelada(s) fora da conta' : ''" />
                <StKpiCard label="Já contando" :value="p1.resumo.contando" icon="bx bx-check-circle" variant="success"
                  :hint="p1.resumo.percentual + '% da meta'" />
                <StKpiCard label="Pendentes" :value="p1.resumo.pendentes" icon="bx bx-time" variant="warning"
                  :hint="p1.resumo.alertas ? p1.resumo.alertas + ' com prazo curto' : 'aguardando o 1º boleto'" />
                <StKpiCard label="Falta para a meta" :value="p1.resumo.faltam" icon="bx bx-target-lock"
                  :variant="p1.resumo.faltam === 0 ? 'success' : 'danger'"
                  :hint="'Potencial: ' + p1.resumo.potencial + (p1.resumo.estourados ? ' · ' + p1.resumo.estourados + ' perdida(s)' : '')" />
              </div>

              <!-- Prévia: quanto já garantiu / ainda pode receber -->
              <div v-if="comissao" class="mm-comissao" :class="'mm-comissao-' + comissao.tom" role="note">
                <span class="mm-comissao-rotulo">{{ comissao.rotulo }}</span>
                <strong v-if="comissao.valor != null" class="mm-comissao-valor">{{ fmtMoeda(comissao.valor) }}</strong>
                <p v-for="(l, i) in comissao.linhas" :key="i" class="mb-1">{{ l }}</p>
                <p v-if="comissao.valor != null" class="mm-comissao-aviso mb-0">
                  Valor calculado sobre a sua <strong>produção pessoal</strong>. Pode sofrer alterações no
                  decorrer do mês, até o fechamento.
                </p>
              </div>
              <div v-if="comissao && comissao.naMesa && comissao.naMesa.valor > 0" class="mm-comissao mm-comissao-perda" role="note">
                <span class="mm-comissao-rotulo">Você está deixando na mesa</span>
                <strong class="mm-comissao-valor">{{ fmtMoeda(comissao.naMesa.valor) }}</strong>
                <p v-for="(l, i) in comissao.naMesa.linhas" :key="'m' + i" class="mb-1">{{ l }}</p>
              </div>

              <div v-if="faixa" class="mm-faixa">
                <template v-if="faixa.atual">Faixa atual: <strong>{{ faixa.atual.percentual }}%</strong> ({{ faixa.atual.minimo }}+ metas).</template>
                <template v-else>Ainda abaixo da primeira faixa.</template>
                <template v-if="faixa.proxima"> Faltam <strong>{{ faixa.proxima.faltam }}</strong> para {{ faixa.proxima.percentual }}%.</template>
              </div>

              <div class="mm-lista-top">
                <div class="mm-filtros" role="group" aria-label="Filtrar adesões">
                  <button type="button" :class="{ ativo: filtroP1 === 'todos' }" :aria-pressed="filtroP1 === 'todos' ? 'true' : 'false'" @click="filtroP1 = 'todos'">Todas</button>
                  <button type="button" :class="{ ativo: filtroP1 === 'pendentes' }" :aria-pressed="filtroP1 === 'pendentes' ? 'true' : 'false'" @click="filtroP1 = 'pendentes'">Pendentes</button>
                  <button type="button" :class="{ ativo: filtroP1 === 'contando' }" :aria-pressed="filtroP1 === 'contando' ? 'true' : 'false'" @click="filtroP1 = 'contando'">Contando</button>
                  <button v-if="p1.resumo.estourados" type="button" :class="{ ativo: filtroP1 === 'perdidas' }" :aria-pressed="filtroP1 === 'perdidas' ? 'true' : 'false'" @click="filtroP1 = 'perdidas'">Perdidas ({{ p1.resumo.estourados }})</button>
                </div>
                <button v-if="placasPendentes.length" type="button" class="btn btn-light btn-sm" @click="copiarPlacas">
                  <i class="bx" :class="copiado ? 'bx-check' : 'bx-copy'" aria-hidden="true"></i>
                  {{ copiado ? "Copiado!" : "Copiar placas pendentes (" + placasPendentes.length + ")" }}
                </button>
                <span class="sr-only" aria-live="polite">{{ copiado ? "Placas copiadas" : "" }}</span>
              </div>
              <div v-if="erroCopia" class="mm-aviso warning" role="alert">
                Não foi possível copiar automaticamente. Placas pendentes: {{ placasPendentes.join(", ") }}
              </div>

              <StResponsiveList
                :fields="camposP1"
                :items="itensP1"
                :row-class="classeLinhaP1"
                caption="Adesões que contam no fechamento"
                empty-icon="bx bx-car"
                :empty-title="p1.itens.length ? 'Nada neste filtro' : 'Nenhuma adesão com vigência em ' + rotuloVigencia"
              >
                <template #cell(vencimento)="{ item }">{{ fmtData(item.vencimento) }}</template>
                <template #cell(prazo)="{ item }">
                  <span :class="{ 'mm-prazo-alerta': item.status.alerta || item.status.key === 'estourado' }">
                    <i v-if="item.status.alerta" class="bx bx-error" aria-hidden="true"></i>
                    {{ textoPrazo(item) }}
                  </span>
                </template>
                <template #cell(status)="{ item }">
                  <span class="mm-badge" :class="classeStatus(item.status.key)">{{ item.status.label }}</span>
                  <span v-if="item.status.anterior && item.status.vigencia_origem" class="mm-badge mm-badge-info mm-badge-origem">
                    Vigência {{ nomeMes(item.status.vigencia_origem.mes) }}
                  </span>
                </template>
                <template #card-footer="{ item }">
                  <small v-if="item.status.conta_no_seguinte_ate" class="mm-muted">
                    Se passar do corte, ainda conta no fechamento seguinte se pago até {{ fmtData(item.status.conta_no_seguinte_ate) }}.
                  </small>
                </template>
              </StResponsiveList>
            </div>
          </section>

          <!-- PASSO 4 — Equipe -->
          <section class="card" aria-labelledby="mm-p3-titulo">
            <div class="card-body">
              <p class="mm-passo">Passo 4</p>
              <h2 id="mm-p3-titulo" class="mm-h2">{{ isRegional ? "Sua equipe" : "Consultores abaixo de você" }}</h2>
              <p class="mm-muted">
                Só aparece quem tem adesões no fechamento de {{ rotuloFechamento }} ("Fech.").
              </p>

              <div v-if="equipe" class="mm-equipe" :class="'mm-equipe-' + equipe.situacao" role="note">
                <strong>Projeção da regra de equipe</strong>
                <span>Próprias contando: {{ equipe.propria }} · Metas de quem ainda não bateu: {{ equipe.soma_downlines_abaixo_meta }}</span>
                <span>{{ textoSituacaoEquipe() }}</span>
                <small class="mm-muted">É só uma projeção informativa — não é o cálculo oficial da comissão.</small>
              </div>

              <StResponsiveList
                :fields="camposP3"
                :items="itensP3"
                caption="Produção da equipe"
                empty-icon="bx bx-group"
                :empty-title="'Ninguém da equipe com adesões no fechamento de ' + rotuloFechamento"
              />
            </div>
          </section>
        </template>
      </template>
    </div>
  </Layout>
</template>

<style scoped>
.mm-page .card {
  margin-bottom: 1rem;
  border-radius: var(--st-radius-lg);
}
.mm-h2 {
  margin: 0 0 0.25rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--st-text);
}
.mm-passo {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--st-brand);
}
.mm-muted {
  color: var(--st-text-muted);
  font-size: 0.875rem;
}
.mm-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}
.mm-convite {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border-radius: var(--st-radius-lg);
  background: var(--st-warning-soft);
  color: var(--st-text);
}
.mm-convite > i {
  font-size: 1.5rem;
  color: var(--st-warning-strong);
}
.mm-convite > div {
  flex: 1 1 14rem;
}
.mm-jornada {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--st-border-soft);
  border-radius: var(--st-radius-lg);
  background: var(--st-surface);
}
.mm-jornada-icone {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: var(--st-brand-soft);
  color: var(--st-brand);
  font-size: 1.25rem;
}
.mm-jornada-texto {
  display: flex;
  flex: 1 1 14rem;
  flex-direction: column;
  font-size: 0.875rem;
  color: var(--st-text-muted);
}
.mm-jornada-texto strong {
  color: var(--st-text);
}
.mm-jornada-erro {
  color: var(--st-danger-strong);
}
.mm-jornada-acoes {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}
.mm-tabs {
  display: inline-flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
  padding: 0.25rem;
  border-radius: 999px;
  background: var(--st-surface);
  border: 1px solid var(--st-border-soft);
}
.mm-tab {
  min-height: 2.5rem;
  padding: 0 1.125rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--st-text-muted);
  font-weight: 600;
}
.mm-tab.ativo {
  background: var(--st-brand);
  color: var(--st-text-inverse);
}
.mm-aviso {
  margin-bottom: 1rem;
  padding: 0.625rem 0.875rem;
  border-radius: var(--st-radius);
  font-size: 0.875rem;
}
.mm-aviso.warning { background: var(--st-warning-soft); color: var(--st-warning-strong); }

.mm-gami-top {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}
.mm-nivel {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.875rem;
  background: var(--st-canvas);
  color: var(--st-text);
}
.mm-nivel-quase { background: var(--st-warning-soft); color: var(--st-warning-strong); }
.mm-nivel-batida,
.mm-nivel-gordura { background: var(--st-success-soft); color: var(--st-success-strong); }
.mm-nivel-superou { background: var(--st-brand-gradient); color: var(--st-text-inverse); }
.mm-corte {
  display: flex;
  gap: 0.625rem;
  padding: 0.75rem 0.875rem;
  border-radius: var(--st-radius);
  background: var(--st-brand-soft);
  font-size: 0.875rem;
  color: var(--st-text);
}
.mm-corte > i {
  font-size: 1.25rem;
  color: var(--st-brand);
}
.mm-corte.urgente {
  background: var(--st-warning-soft);
}
.mm-corte.urgente > i {
  color: var(--st-warning-strong);
}
.mm-corte-extra {
  margin-top: 0.25rem;
}
.mm-corte-atencao {
  padding-top: 0.5rem;
  border-top: 1px solid var(--st-border);
  color: var(--st-text);
}
.mm-comissao {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1rem;
  padding: 1rem 1.125rem;
  border-radius: var(--st-radius-lg);
  border: 1px solid var(--st-border);
  border-left-width: 6px;
  background: var(--st-canvas);
  font-size: 0.9375rem;
  color: var(--st-text);
}
.mm-comissao-rotulo {
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.mm-comissao-valor {
  font-size: 2.25rem;
  font-weight: 800;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}
.mm-comissao-aviso {
  font-size: 0.8125rem;
  opacity: 0.85;
}
.mm-comissao-perda {
  border-color: var(--st-danger);
  background: var(--st-danger-soft);
}
.mm-comissao-perda .mm-comissao-rotulo,
.mm-comissao-perda .mm-comissao-valor {
  color: var(--st-danger-strong);
}
.mm-comissao-chance {
  border-color: var(--st-warning);
  background: var(--st-warning-soft);
}
.mm-comissao-chance .mm-comissao-rotulo,
.mm-comissao-chance .mm-comissao-valor {
  color: var(--st-warning-strong);
}
.mm-comissao-ok {
  border-color: var(--st-success);
  background: var(--st-success-soft);
}
.mm-comissao-ok .mm-comissao-rotulo,
.mm-comissao-ok .mm-comissao-valor {
  color: var(--st-success-strong);
}
@media (max-width: 575.98px) {
  .mm-comissao-valor {
    font-size: 1.75rem;
  }
}
.mm-faixa {
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  border-left: 3px solid var(--st-brand);
  background: var(--st-canvas);
  font-size: 0.875rem;
}
.mm-lista-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.mm-filtros {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.mm-filtros button {
  min-height: 2.25rem;
  padding: 0 0.875rem;
  border: 1px solid var(--st-border);
  border-radius: 999px;
  background: var(--st-surface);
  color: var(--st-text-muted);
  font-size: 0.8125rem;
  font-weight: 600;
}
.mm-filtros button.ativo {
  border-color: var(--st-brand);
  background: var(--st-brand-soft);
  color: var(--st-brand);
}
.mm-badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}
.mm-badge-ok { background: var(--st-status-approved-bg); color: var(--st-status-approved-text); }
.mm-badge-info { background: var(--st-status-returned-bg); color: var(--st-status-returned-text); }
.mm-badge-warn { background: var(--st-status-pending-bg); color: var(--st-status-pending-text); }
.mm-badge-bad { background: var(--st-status-rejected-bg); color: var(--st-status-rejected-text); }
.mm-badge-neutral { background: var(--st-canvas); color: var(--st-text-muted); }
.mm-badge-origem { margin-left: 0.25rem; }
.mm-prazo-alerta {
  font-weight: 700;
  color: var(--st-danger-strong);
}
.mm-page >>> .mm-row-warn td {
  background: var(--st-warning-soft);
}
.mm-page >>> .mm-row-bad td {
  background: var(--st-danger-soft);
}
.mm-page >>> .st-rl-card.mm-row-warn {
  border-color: var(--st-warning);
}
.mm-page >>> .st-rl-card.mm-row-bad {
  border-color: var(--st-danger);
}
.mm-nota {
  display: flex;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border-radius: var(--st-radius);
  background: var(--st-brand-soft);
  font-size: 0.875rem;
}
.mm-nota > i {
  font-size: 1.125rem;
  color: var(--st-brand);
}
.mm-equipe {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.875rem;
  border-radius: var(--st-radius);
  border-left: 4px solid var(--st-border);
  background: var(--st-canvas);
  font-size: 0.875rem;
}
.mm-equipe-sozinho,
.mm-equipe-equipe {
  border-left-color: var(--st-success);
}
.mm-equipe-ainda_nao {
  border-left-color: var(--st-warning);
}
@media (max-width: 1024.98px) {
  .mm-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 575.98px) {
  .mm-tabs {
    display: flex;
  }
  .mm-tab {
    flex: 1;
  }
  .mm-kpis {
    gap: 0.5rem;
  }
  .mm-lista-top > .btn {
    width: 100%;
  }
}
</style>
